/**
 * WorkspaceTabs Component
 *
 * Centered segmented control for switching between Report and Graph workspaces.
 * Clean, minimal design that stays out of the way.
 */

import React from 'react';
import {
  WorkspaceType,
  WorkspaceSwitchProposal,
} from '../../types/workspace';

interface WorkspaceTabsProps {
  activeWorkspace: WorkspaceType;
  onWorkspaceChange: (workspace: WorkspaceType) => void;
  pendingSwitch?: WorkspaceSwitchProposal | null;
  onAcceptSwitch?: () => void;
  onRejectSwitch?: () => void;
  isAgentActive?: boolean;
  /** Disables every tab — used during the Welcome-to-Research empty
   *  state, where there's no report and no graph yet, so switching tabs
   *  is a no-op. Pill stays visible but greyed out. */
  disabled?: boolean;
}

const TABS: { id: WorkspaceType; label: string; icon: React.ReactNode }[] = [
  {
    id: 'report',
    label: 'Report',
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
      </svg>
    ),
  },
  {
    id: 'graph',
    label: 'Graph',
    icon: (
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
        <circle cx="6" cy="6" r="2.5" />
        <circle cx="18" cy="6" r="2.5" />
        <circle cx="12" cy="18" r="2.5" />
        <path strokeLinecap="round" d="M8.2 7.4L10.5 16M15.8 7.4L13.5 16" />
      </svg>
    ),
  },
];

export default function WorkspaceTabs({
  activeWorkspace,
  onWorkspaceChange,
  pendingSwitch,
  onAcceptSwitch,
  onRejectSwitch,
  isAgentActive,
  disabled: allDisabled,
}: WorkspaceTabsProps) {
  return (
    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1.5">
      {/* Tab bar — floating segmented control */}
      <div className="inline-flex items-center gap-0.5 p-0.5 rounded-lg bg-accent/90 backdrop-blur-md border border-border-default/50 shadow-lg">
        {TABS.map((tab) => {
          const isActive = activeWorkspace === tab.id;
          const tabDisabled = allDisabled || (!isActive && isAgentActive);
          const tabTitle = allDisabled
            ? 'Available once a report or graph has been generated'
            : tabDisabled
              ? 'Cannot switch while agent is working'
              : undefined;

          return (
            <button
              key={tab.id}
              onClick={tabDisabled ? undefined : () => onWorkspaceChange(tab.id)}
              disabled={tabDisabled}
              title={tabTitle}
              className={`
                inline-flex items-center gap-1.5 px-3.5 py-1 rounded-md text-xs font-medium
                transition-all duration-150 select-none
                ${tabDisabled
                  ? 'text-fg-secondary cursor-not-allowed opacity-60'
                  : isActive
                    ? 'bg-fg-secondary/70 text-fg shadow-sm'
                    : 'text-fg-tertiary hover:text-fg hover:bg-accent-hover/40'
                }
              `}
            >
              {tab.icon}
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Workspace switch suggestion */}
      {pendingSwitch && onAcceptSwitch && onRejectSwitch && (
        <div className="inline-flex items-center gap-3 px-3 py-1.5 bg-warning-bg backdrop-blur-md rounded-lg border border-warning shadow-lg">
          <span className="text-warning text-xs">
            Switch to <span className="font-medium capitalize">{pendingSwitch.to_workspace}</span>
            {pendingSwitch.reason && ` — ${pendingSwitch.reason}`}
          </span>
          <button
            onClick={onAcceptSwitch}
            className="px-2 py-0.5 text-xs font-medium text-warning bg-warning/40 rounded hover:bg-warning/60 transition-colors"
          >
            Switch
          </button>
          <button
            onClick={onRejectSwitch}
            className="text-xs text-warning/60 hover:text-warning transition-colors"
          >
            Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
